import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { Search, Receipt, User, BookOpen, Wallet, GraduationCap, AlertCircle, CheckCircle, X, MessageSquare } from 'lucide-react';
import { toPng } from 'html-to-image';
import './InvoiceManager.css';
import { useConfig } from '../ConfigContext';



const parseScheduleDays = (tgb) => {
   if (!tgb) return [];
   const normalized = tgb.toLowerCase().replace(/thứ /g, 't').replace(/thứ/g, 't').replace(/chủ nhật/g, 'cn');
   const days = [];
   if (normalized.includes('t2')) days.push(1);
   if (normalized.includes('t3')) days.push(2);
   if (normalized.includes('t4')) days.push(3);
   if (normalized.includes('t5')) days.push(4);
   if (normalized.includes('t6')) days.push(5);
   if (normalized.includes('t7')) days.push(6);
   if (normalized.includes('cn')) days.push(0);
   return days;
};

const formatMonthYear = (dateStr) => {
   if (!dateStr) return '';
   const d = new Date(dateStr);
   return `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const getQRUrl = (hoaDon, walletsConfig) => {
   if (!walletsConfig || !hoaDon.hinhthuc) return null;
   const hinhThucTrim = String(hoaDon.hinhthuc).trim();
   const matchedWallet = walletsConfig.find(w => String(w.name).trim() === hinhThucTrim);
   if (matchedWallet && matchedWallet.bankId && matchedWallet.accNo) {
      const amountStr = (hoaDon.tongcong || "0").toString().replace(/\D/g, "");
      const info = encodeURIComponent(`${hoaDon.mahv} Dong HP Thang ${formatMonthYear(hoaDon.ngaybatdau)}`);
      return `https://img.vietqr.io/image/${matchedWallet.bankId}-${matchedWallet.accNo}-compact2.png?amount=${amountStr}&addInfo=${info}&accountName=${encodeURIComponent(matchedWallet.accName || '')}`;
   }
   return null;
};

const calculateEndDateBySessions = (startDateStr, numSessions, activeDays) => {
   if (!startDateStr || !numSessions || activeDays.length === 0) return '';
   let current = new Date(startDateStr);
   let sessionsFound = 0;
   let maxDaysToCheck = 3650;

   while (sessionsFound < numSessions && maxDaysToCheck > 0) {
      if (activeDays.includes(current.getDay())) {
         sessionsFound++;
         if (sessionsFound === parseInt(numSessions, 10)) {
            break;
         }
      }
      current.setDate(current.getDate() + 1);
      maxDaysToCheck--;
   }
   if (sessionsFound > 0) {
      return current.toISOString().split('T')[0];
   }
   return '';
};

export default function InvoiceManager() {
   const { config } = useConfig();
   const walletsConfig = (config ? [
      { id: 'vi1', name: config.vi1?.name || '', bankId: config.vi1?.bankId || '', accNo: config.vi1?.accNo || '', accName: config.vi1?.accName || '' },
      { id: 'vi2', name: config.vi2?.name || '', bankId: config.vi2?.bankId || '', accNo: config.vi2?.accNo || '', accName: config.vi2?.accName || '' },
      { id: 'vi3', name: config.vi3?.name || '', bankId: config.vi3?.bankId || '', accNo: config.vi3?.accNo || '', accName: config.vi3?.accName || '' },
      { id: 'vi4', name: config.vi4?.name || '', bankId: config.vi4?.bankId || '', accNo: config.vi4?.accNo || '', accName: config.vi4?.accName || '' }
   ].filter(w => w.name && w.name.trim() !== '') : []);

   const [students, setStudents] = useState([]);
   const [classes, setClasses] = useState([]);
   const [employees, setEmployees] = useState([]);
   const [searchTerm, setSearchTerm] = useState('');
   const [selectedStudent, setSelectedStudent] = useState(null);
   const [activeClass, setActiveClass] = useState(null);
   const [classTeacher, setClassTeacher] = useState(null);

   const [isSaving, setIsSaving] = useState(false);
   const [message, setMessage] = useState({ type: '', text: '' });
   const [warningModal, setWarningModal] = useState({ isOpen: false, title: '', message: '' });
   const [successModal, setSuccessModal] = useState({ isOpen: false, title: '', message: '' });
   const [downloadingInvoice, setDownloadingInvoice] = useState(null);
   const [downloadingNotice, setDownloadingNotice] = useState(null);
   const [previewImg, setPreviewImg] = useState(null);
   const [studySummary, setStudySummary] = useState(null);
   const [recentSourceText, setRecentSourceText] = useState('');
   const [showMobileDetails, setShowMobileDetails] = useState(false);
   const [invoiceData, setInvoiceData] = useState({
      loaiDong: 'Tháng',
      soLuong: 1,
      ngayBatDau: new Date().toISOString().split('T')[0],
      ngayKetThuc: '',
      hocphi: 0,
      donGia: 0,
      giamHocphi: 0,
      daDong: 0,
      hinhThuc: (config && (config.vi1?.name || config.vi2?.name || config.vi3?.name || config.vi4?.name)) ? (config.vi1?.name || config.vi2?.name || config.vi3?.name || config.vi4?.name) : 'Tiền mặt',
      ghiChu: ''
   });

   const auth = JSON.parse(localStorage.getItem('auth_session') || '{}');
   const cashier = auth.user?.tennv || auth.user?.username || 'Thu Ngân';

   const [noCu, setNoCu] = useState(0);

   const fetchBaseData = async () => {
      try {
         const { data: stRaw } = await supabase.from('tbl_hv').select('*').neq('trangthai', 'Đã Nghỉ').order('tenhv', { ascending: true });
         const { data: cls } = await supabase.from('tbl_lop').select('*');
         const { data: emp } = await supabase.from('tbl_nv').select('*');
         const { data: lh } = await supabase.from('tbl_lichhoc_hv').select('mahv, malop, lichhoc, giohoc');

         const st = (stRaw || []).map(s => ({
            ...s,
            malop_list: (lh || []).filter(l => l.mahv === s.mahv).map(l => l.malop)
         }));

         setStudents(st || []);
         setClasses(cls || []);
         setEmployees(emp || []);
      } catch (err) {
         console.error(err);
      }
   };

   useEffect(() => {
      fetchBaseData();
   }, []);

   useEffect(() => {
      if (downloadingInvoice) {
         const processPng = async () => {
            try {
               await new Promise(r => setTimeout(r, 1000));
               const node = document.getElementById('download-invoice-node');
               if (node) {
                  // Capture setup: force visible and at top
                  node.style.position = 'fixed';
                  node.style.top = '0';
                  node.style.left = '0';
                  node.style.zIndex = '9999';
                  node.style.opacity = '1';
                  node.style.visibility = 'visible';

                  const images = node.querySelectorAll('img');
                  await Promise.all(Array.from(images).map(img => {
                     if (img.complete) return Promise.resolve();
                     return new Promise(res => { img.onload = res; img.onerror = res; setTimeout(res, 5000); });
                  }));
                  await new Promise(r => setTimeout(r, 500));

                  const dataUrl = await toPng(node, { cacheBust: true, backgroundColor: '#ffffff' });

                  // Restore hide
                  node.style.position = 'static';
                  node.style.opacity = '0.01';

                  if (window.innerWidth <= 991) {
                     setPreviewImg(dataUrl);
                  } else {
                     const link = document.createElement('a');
                     link.download = `HoaDon_${downloadingInvoice.tenhv}_${downloadingInvoice.mahd}.png`;
                     link.href = dataUrl;
                     document.body.appendChild(link);
                     link.click();
                     document.body.removeChild(link);
                  }
               }
            } catch (err) {
               console.error('Lỗi xuất PNG:', err);
            } finally {
               setDownloadingInvoice(null);
            }
         };
         processPng();
      }
   }, [downloadingInvoice]);

   useEffect(() => {
      if (downloadingNotice) {
         const processPng = async () => {
            try {
               await new Promise(r => setTimeout(r, 1500));
               const node = document.getElementById('download-notice-node');
               if (node) {
                  // Capture setup
                  node.style.position = 'fixed';
                  node.style.top = '0';
                  node.style.left = '0';
                  node.style.zIndex = '9999';
                  node.style.opacity = '1';
                  node.style.visibility = 'visible';

                  const images = node.querySelectorAll('img');
                  await Promise.all(Array.from(images).map(img => {
                     if (img.complete) return Promise.resolve();
                     return new Promise(res => { img.onload = res; img.onerror = res; setTimeout(res, 5000); });
                  }));
                  await new Promise(r => setTimeout(r, 600));

                  const dataUrl = await toPng(node, { cacheBust: true, backgroundColor: '#ffffff' });

                  // Restore hide
                  node.style.position = 'static';
                  node.style.opacity = '0.01';

                  if (window.innerWidth <= 991) {
                     setPreviewImg(dataUrl);
                  } else {
                     const link = document.createElement('a');
                     link.download = `ThongBao_${downloadingNotice.tenhv}_${downloadingNotice.mahd}.png`;
                     link.href = dataUrl;
                     document.body.appendChild(link);
                     link.click();
                     document.body.removeChild(link);
                  }
               }
            } catch (err) {
               console.error('Notice capture error:', err);
            } finally {
               setDownloadingNotice(null);
            }
         };
         processPng();
      }
   }, [downloadingNotice]);

   const calculateOldDebt = async (mahv) => {
      try {
         let totalDebt = 0;
         const parseCur = (v) => parseInt(String(v).replace(/,/g, ''), 10) || 0;

         const { data: hd } = await supabase.from('tbl_hd').select('conno, daxoa').eq('mahv', mahv);
         const { data: bill } = await supabase.from('tbl_billhanghoa').select('conno, daxoa').eq('mahv', mahv);

         (hd || []).filter(x => (x.daxoa || '').toLowerCase() !== 'đã xóa').forEach(x => totalDebt += parseCur(x.conno));
         (bill || []).filter(x => (x.daxoa || '').toLowerCase() !== 'đã xóa').forEach(x => totalDebt += parseCur(x.conno));

         setNoCu(totalDebt);
      } catch (e) {
         console.error(e);
      }
   };


    const handleSelectStudent = async (st) => {
       setSelectedStudent(st);
       setShowMobileDetails(true);
       setMessage({ type: '', text: '' });
       calculateOldDebt(st.mahv);
 
       const firstMalop = st.malop_list && st.malop_list.length > 0 ? st.malop_list[0] : null;
       await updateClassContext(firstMalop, st);
    };

    const updateClassContext = async (malop, student) => {
       if (!malop || !student) return;
       const stClass = classes.find(c => c.malop === malop);
       
       // Lấy lịch học theo cấu hình
       let effectiveSchedule = '';
       if (config?.lichhoctheolop === false) {
          const { data: hvSched } = await supabase.from('tbl_lichhoc_hv').select('lichhoc').eq('mahv', student.mahv).eq('malop', malop).single();
          effectiveSchedule = hvSched?.lichhoc || '';
       } else {
          const { data: lopSched } = await supabase.from('tbl_lichhoc_lop').select('lichhoc').eq('malop', malop).single();
          effectiveSchedule = lopSched?.lichhoc || '';
       }

       // Tạo bản clone hoặc update object activeClass để chứa lichhoc đúng
       const enrichedClass = { ...stClass, thoigianbieu: effectiveSchedule };
       setActiveClass(enrichedClass);
 
       let defaultFee = 0;
       let tchr = null;
       if (stClass) {
          tchr = employees.find(e => e.manv === stClass.manv);
          if (stClass.hocphi) {
             const numbers = stClass.hocphi.replace(/,/g, '').match(/\d+/g);
             if (numbers && numbers.length > 0) {
                const maxNum = Math.max(...numbers.map(Number));
                if (maxNum > 1000) defaultFee = maxNum;
             }
          }
       }
       setClassTeacher(tchr);
        // Default values from class tuition template
        let loaiDong = 'Tháng';
        let soLuong = 1;

        if (stClass?.hocphi) {
           const firstOpt = stClass.hocphi.split('\n').filter(Boolean)[0] || '';
           const qtyMatch = firstOpt.match(/(?:^|[^0-9])(\d+)\s*(?:buổi|tháng|khóa|tuần)/i);
           if (qtyMatch && qtyMatch[1]) soLuong = parseInt(qtyMatch[1], 10);
           
           const lowOpt = firstOpt.toLowerCase();
           if (lowOpt.includes('buổi')) loaiDong = 'Buổi';
           else if (lowOpt.includes('tháng')) loaiDong = 'Tháng';
           else if (lowOpt.includes('khóa')) loaiDong = 'Khóa';
           else if (lowOpt.includes('tuần')) loaiDong = 'Tuần';
        }

        let hocphi = defaultFee;
        let giamHocphi = 0;
        let hinhThuc = walletsConfig.length > 0 ? walletsConfig[0].name : 'Tiền mặt';
        let ghiChu = '';
  
        setStudySummary(null);
        setRecentSourceText('');
 
       // Load recent HD or Thong bao for student + specific class
       let startStr = new Date().toISOString().split('T')[0];
       let endMonthStr = '';
 
       try {
          const [{ data: allHDs }, { data: allTBs }] = await Promise.all([
             supabase.from('tbl_hd').select('*').eq('mahv', student.mahv).eq('malop', malop),
             supabase.from('tbl_thongbao').select('*').eq('mahv', student.mahv).eq('malop', malop)
          ]);
 
          const validHDs = (allHDs || []).filter(x => (x.daxoa || '').toLowerCase() !== 'đã xóa');
          const validTBs = (allTBs || []).filter(x => (x.daxoa || '').toLowerCase() !== 'đã xóa');
 
          let allDocs = [...validHDs, ...validTBs];
          allDocs.sort((a, b) => new Date(b.ngaylap || 0) - new Date(a.ngaylap || 0));
          const recentDoc = allDocs.length > 0 ? allDocs[0] : null;
 
          validHDs.sort((a, b) => new Date(b.ngayketthuc || b.ngaylap || 0) - new Date(a.ngayketthuc || a.ngaylap || 0));
          const recentHD = validHDs.length > 0 ? validHDs[0] : null;
 
          if (recentDoc) {
             setRecentSourceText(recentDoc.mahd?.startsWith('TB') ? `Lấy dữ liệu từ Thông báo HP gần nhất lớp này (${recentDoc.mahd})` : `Lấy dữ liệu từ Hóa đơn gần nhất lớp này (${recentDoc.mahd})`);
             const parseCur = (v) => parseInt(String(v).replace(/,/g, ''), 10) || 0;
             hocphi = parseCur(recentDoc.hocphi);
             giamHocphi = parseCur(recentDoc.giamhocphi);
             hinhThuc = recentDoc.hinhthuc || (walletsConfig.length > 0 ? walletsConfig[0].name : 'Tiền mặt');
             if (walletsConfig.length > 0 && !walletsConfig.some(w => w.name === hinhThuc)) {
                hinhThuc = walletsConfig[0].name;
             }
             ghiChu = recentDoc.ghichu || '';
             if (recentDoc.ngaybatdau) startStr = recentDoc.ngaybatdau;
             if (recentDoc.ngayketthuc) endMonthStr = recentDoc.ngayketthuc;
             if (recentDoc.sobuoihoc) {
                const qtyMatch = recentDoc.sobuoihoc.match(/^(\d+)/);
                if (qtyMatch) soLuong = parseInt(qtyMatch[1], 10);
                const sbhLower = recentDoc.sobuoihoc.toLowerCase();
                if (sbhLower.includes('buổi')) loaiDong = 'Buổi';
                else if (sbhLower.includes('tháng')) loaiDong = 'Tháng';
                else if (sbhLower.includes('khóa')) loaiDong = 'Khóa';
                else if (sbhLower.includes('tuần')) loaiDong = 'Tuần';
             }
          }
 
          // Thống kê điểm danh
          if (recentHD && recentHD.ngaybatdau && recentHD.ngayketthuc && effectiveSchedule) {
             const activeDays = parseScheduleDays(effectiveSchedule);
             let tongBuoi = 0;
             let cDate = new Date(recentHD.ngaybatdau);
             const eDate = new Date(recentHD.ngayketthuc);
             let safeCount = 0;
             while (cDate <= eDate && safeCount < 1000) {
                if (activeDays.includes(cDate.getDay())) tongBuoi++;
                cDate.setDate(cDate.getDate() + 1);
                safeCount++;
             }
 
             const { data: attendance } = await supabase.from('tbl_diemdanh').select('*')
                .eq('mahv', student.mahv).eq('malop', malop)
                .gte('ngay', recentHD.ngaybatdau).lte('ngay', recentHD.ngayketthuc);
 
             const normalizeStatus = (s) => (s || '').trim().toLowerCase();
             let daHoc = 0, nghiPhep = 0, nghiKhongPhep = 0;
             (attendance || []).forEach(att => {
                const s = normalizeStatus(att.trangthai);
                if (s === 'có mặt') daHoc++;
                else if (s === 'nghỉ phép') nghiPhep++;
                else if (s === 'nghỉ không phép') nghiKhongPhep++;
             });
             setStudySummary({ daHoc, nghiPhep, nghiKhongPhep, tongBuoi, sourceHd: recentHD.mahd });
          }
       } catch (err) { console.error(err); }
 
       if (!endMonthStr) {
          const unit = (loaiDong || '').toLowerCase().trim();
          if (unit.includes('tháng') || unit.includes('khóa')) {
             const tempDate = new Date(startStr);
             if (!isNaN(tempDate.getTime())) {
                tempDate.setMonth(tempDate.getMonth() + (parseInt(soLuong) || 1));
                endMonthStr = tempDate.toISOString().split('T')[0];
             }
          } else if (unit.includes('buổi') && effectiveSchedule) {
             const activeDays = parseScheduleDays(effectiveSchedule);
             if (activeDays.length > 0) {
                endMonthStr = calculateEndDateBySessions(startStr, (parseInt(soLuong) || 1), activeDays);
             }
          } else if (unit.includes('tuần')) {
             const startD = new Date(startStr);
             if (!isNaN(startD.getTime())) {
                startD.setDate(startD.getDate() + (parseInt(soLuong) || 1) * 7);
                endMonthStr = startD.toISOString().split('T')[0];
             }
          }
          if (!endMonthStr) {
             const nextM = new Date(startStr);
             if (!isNaN(nextM.getTime())) {
                nextM.setMonth(nextM.getMonth() + 1);
                endMonthStr = nextM.toISOString().split('T')[0];
             }
          }
       }
 
       setInvoiceData({
          loaiDong, soLuong, ngayBatDau: startStr, ngayKetThuc: endMonthStr,
          hocphi, donGia: hocphi / (soLuong || 1), giamHocphi, daDong: 0, hinhThuc, ghiChu
       });
    };

   const showMessage = (type, text) => {
      setMessage({ type, text });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
   };

   const formatCurrency = (val) => {
      if (!val && val !== 0) return '';
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
   };

   const handleFinanceInput = (field, e) => {
      const rawValue = e.target.value.replace(/,/g, '').replace(/\D/g, '');
      const num = parseInt(rawValue, 10) || 0;
      setInvoiceData(prev => {
         let next = { ...prev, [field]: num };
         if (field === 'hocphi' && prev.soLuong) {
            next.donGia = num / prev.soLuong;
         }
         return next;
      });
   };

   const handleSelectTuitionPackage = (optText) => {
      let newInv = { ...invoiceData };

      const numbers = optText.replace(/,/g, '').match(/\d{4,}/g);
      if (numbers) {
         newInv.hocphi = Math.max(...numbers.map(Number));
      }

      const isBuoi = optText.toLowerCase().includes('buổi');
      const isThang = optText.toLowerCase().includes('tháng');
      const isKhoa = optText.toLowerCase().includes('khóa');
      const isTuan = optText.toLowerCase().includes('tuần');

      if (isBuoi) newInv.loaiDong = 'Buổi';
      else if (isThang) newInv.loaiDong = 'Tháng';
      else if (isKhoa) newInv.loaiDong = 'Khóa';
      else if (isTuan) newInv.loaiDong = 'Tuần';

      const qtyMatch = optText.match(/(?:^|[^0-9])(\d+)\s*(?:buổi|tháng|tuần)/i);
      if (qtyMatch && qtyMatch[1]) {
         newInv.soLuong = parseInt(qtyMatch[1], 10);
         newInv.donGia = newInv.hocphi / (newInv.soLuong || 1);
      } else {
         newInv.donGia = newInv.hocphi / (parseInt(newInv.soLuong) || 1);
      }

      const unit = (newInv.loaiDong || '').toLowerCase().trim();
      if (unit.includes('tháng') || unit.includes('khóa')) {
         const startD = new Date(newInv.ngayBatDau);
         if (!isNaN(startD.getTime())) {
            startD.setMonth(startD.getMonth() + (parseInt(newInv.soLuong) || 1));
            newInv.ngayKetThuc = startD.toISOString().split('T')[0];
         }
      } else if (unit.includes('buổi') && newInv.ngayBatDau && newInv.soLuong && activeClass?.thoigianbieu) {
         const activeDays = parseScheduleDays(activeClass.thoigianbieu);
         if (activeDays.length > 0) {
            newInv.ngayKetThuc = calculateEndDateBySessions(newInv.ngayBatDau, (parseInt(newInv.soLuong) || 1), activeDays);
         }
      } else if (unit.includes('tuần') && newInv.ngayBatDau && newInv.soLuong) {
         const startD = new Date(newInv.ngayBatDau);
         if (!isNaN(startD.getTime())) {
            startD.setDate(startD.getDate() + (parseInt(newInv.soLuong) || 1) * 7);
            newInv.ngayKetThuc = startD.toISOString().split('T')[0];
         }
      }

      setInvoiceData(newInv);
   };

   const handleFormChange = (field, val) => {
      let newInv = { ...invoiceData, [field]: val };

      if (field === 'soLuong' || field === 'ngayBatDau' || field === 'loaiDong') {
         if (field === 'soLuong' && process.env.REACT_APP_CALC_FEE_BY_SESSION === 'true') {
            const parsedQty = parseInt(val);
            if (!isNaN(parsedQty) && parsedQty > 0 && newInv.loaiDong === 'Buổi') {
               newInv.hocphi = Math.round((invoiceData.donGia || 0) * parsedQty);
            }
         }

         const unit = (newInv.loaiDong || '').toLowerCase().trim();
         if (unit.includes('tháng') || unit.includes('khóa')) {
            const startD = new Date(newInv.ngayBatDau);
            if (!isNaN(startD.getTime())) {
               startD.setMonth(startD.getMonth() + (parseInt(newInv.soLuong) || 1));
               newInv.ngayKetThuc = startD.toISOString().split('T')[0];
            }
         } else if (unit.includes('buổi') && newInv.ngayBatDau && newInv.soLuong && activeClass?.thoigianbieu) {
            const activeDays = parseScheduleDays(activeClass.thoigianbieu);
            if (activeDays.length > 0) {
               newInv.ngayKetThuc = calculateEndDateBySessions(newInv.ngayBatDau, (parseInt(newInv.soLuong) || 1), activeDays);
            }
         } else if (unit.includes('tuần') && newInv.ngayBatDau && newInv.soLuong) {
            const startD = new Date(newInv.ngayBatDau);
            if (!isNaN(startD.getTime())) {
               startD.setDate(startD.getDate() + (parseInt(newInv.soLuong) || 1) * 7);
               newInv.ngayKetThuc = startD.toISOString().split('T')[0];
            }
         }
      }
      setInvoiceData(newInv);
   };

   const handleExportNotice = () => {
      setDownloadingNotice({
         mahd: 'TB-PREVIEW',
         ngaylap: new Date().toISOString(),
         tenhv: selectedStudent.tenhv,
         mahv: selectedStudent.mahv,
         sdt: selectedStudent.sdt,
         tenlop: activeClass?.tenlop || '',
         ngaybatdau: invoiceData.ngayBatDau || null,
         ngayketthuc: invoiceData.ngayKetThuc || null,
         hocphi: formatCurrency(invoiceData.hocphi) + ' đ',
         giamhocphi: formatCurrency(invoiceData.giamHocphi) + ' đ',
         tongcong: formatCurrency(tongCong),
         hinhthuc: invoiceData.hinhThuc,
         ghichu: invoiceData.ghiChu,
         sobuoihoc: `${invoiceData.soLuong} ${invoiceData.loaiDong}`
      });
   };

   const handleSaveInvoice = async () => {
      setIsSaving(true);
      try {
         if (invoiceData.ngayBatDau && invoiceData.ngayKetThuc) {
            const { data: dupData, error: dupErr } = await supabase.from('tbl_hd')
               .select('mahd, daxoa')
               .eq('mahv', selectedStudent.mahv)
               .eq('malop', activeClass?.malop || '')
               .eq('ngaybatdau', invoiceData.ngayBatDau)
               .eq('ngayketthuc', invoiceData.ngayKetThuc);
 
            // Filter out logical duplicates ignoring logically "deleted" records
            const validDups = (dupData || []).filter(d => (d.daxoa || '').toLowerCase() !== 'đã xóa');

            if (!dupErr && validDups.length > 0) {
               setWarningModal({
                  isOpen: true,
                  title: 'Cảnh Báo Đóng Trùng Học Phí',
                  message: 'Học viên này đã nộp học phí cho khoảng thời gian này rồi. Để tránh tính nhầm tiền, hệ thống sẽ từ chối xuất hóa đơn. Vui lòng kiểm tra lại Hóa Đơn cũ!'
               });
               setIsSaving(false);
               return;
            }
         }

         const { data: recentHD } = await supabase.from('tbl_hd').select('mahd').order('mahd', { ascending: false }).limit(1);
         let nextNum = 1;
         if (recentHD && recentHD.length > 0 && recentHD[0].mahd) {
            const numPart = recentHD[0].mahd.replace(/\D/g, '');
            if (!isNaN(parseInt(numPart, 10))) nextNum = parseInt(numPart, 10) + 1;
         }
         const newMaHD = `HD${String(nextNum).padStart(5, '0')}`;

         // Local tz for ngaylap
         const localNow = new Date(new Date() - new Date().getTimezoneOffset() * 60000).toISOString();

         const insertData = {
            mahd: newMaHD,
            ngaylap: localNow,
            mahv: selectedStudent.mahv,
            tenlop: activeClass?.tenlop || '',
            ngaybatdau: invoiceData.ngayBatDau || null,
            ngayketthuc: invoiceData.ngayKetThuc || null,
            nhanvien: cashier,
            hocphi: formatCurrency(invoiceData.hocphi),
            giamhocphi: formatCurrency(invoiceData.giamHocphi),
            phuphi: '0',
            dsphuphi: '',
            tongcong: formatCurrency(tongCong),
            dadong: formatCurrency(invoiceData.daDong),
            conno: formatCurrency(conLai),
            hinhthuc: invoiceData.hinhThuc,
            ghichu: `${invoiceData.ghiChu}${tienTruNghi > 0 ? ` (Trừ ${studySummary.nghiPhep} buổi nghỉ phép: ${formatCurrency(tienTruNghi)}đ)` : ''}`,
            daxoa: null,
            sobuoihoc: `${invoiceData.soLuong} ${invoiceData.loaiDong}`,
            malop: activeClass?.malop || ''
         };

         const res = await supabase.from('tbl_hd').insert([insertData]);

         if (res.error) throw res.error;

         // show thành công
         setSuccessModal({
            isOpen: true,
            title: 'Xuất Hóa Đơn Thành Công 🎉',
            message: `Hóa đơn ${newMaHD} đã được tạo thành công!`
         });

         setDownloadingInvoice({
            mahd: newMaHD,
            ngaylap: localNow,
            tenhv: selectedStudent.tenhv,
            sdt: selectedStudent.sdt,
            tenlop: activeClass?.tenlop || '',
            ngaybatdau: invoiceData.ngayBatDau || null,
            ngayketthuc: invoiceData.ngayKetThuc || null,
            hocphi: formatCurrency(invoiceData.hocphi),
            giamhocphi: formatCurrency(invoiceData.giamHocphi),
            nocu: formatCurrency(noCu),
            tongcong: formatCurrency(tongCong),
            dadong: formatCurrency(invoiceData.daDong),
            conno: formatCurrency(conLai),
            ghichu: `${invoiceData.ghiChu}${tienTruNghi > 0 ? ` (Trừ ${studySummary.nghiPhep} buổi nghỉ phép: ${formatCurrency(tienTruNghi)}đ)` : ''}`,
            nhanvien: cashier,
            sobuoihoc: `${invoiceData.soLuong} ${invoiceData.loaiDong}`
         });

         // Reload old debt dynamically mimicking real-time refresh
         calculateOldDebt(selectedStudent.mahv);
         setInvoiceData(prev => ({ ...prev, daDong: 0 })); // reset form state for next bill
      } catch (err) {
         console.error(err);
         showMessage('error', 'Lỗi xuất hóa đơn: ' + err.message);
      } finally {
         setIsSaving(false);
      }
   };

   const filteredStudents = students.filter(s =>
      (s.tenhv && s.tenhv.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (s.sdt && s.sdt.includes(searchTerm)) ||
      (s.mahv && s.mahv.toLowerCase().includes(searchTerm.toLowerCase()))
   );

   const tienTruNghi = (config?.trutiennghi && studySummary && activeClass?.trutiennghi)
      ? (studySummary.nghiPhep * parseInt(String(activeClass.trutiennghi).replace(/,/g, ''), 10))
      : 0;

   const tongCong = noCu + invoiceData.hocphi - invoiceData.giamHocphi - tienTruNghi;

   // Auto fill daDong in InvoiceManager
   useEffect(() => {
      setInvoiceData(prev => ({ ...prev, daDong: tongCong }));
   }, [tongCong]);

   const conLai = tongCong - invoiceData.daDong;

   return (
      <div className={`invoice-manager animate-fade-in ${showMobileDetails ? 'mobile-show-details' : ''}`}>

         {/* TRÁI: TÌM KIẾM LIST HỌC VIÊN */}
         <div className="im-left-pane">
            <div className="im-search">
               <Search size={20} className="text-muted" />
               <input
                  type="text"
                  placeholder="Tìm mã HV, tên, SĐT..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
               />
            </div>
            <div className="im-student-list">
               {filteredStudents.length > 0 ? filteredStudents.map(st => {
                  const isActive = selectedStudent?.mahv === st.mahv;
                  return (
                     <div key={st.mahv} className={`im-student-card ${isActive ? 'active' : ''}`} onClick={() => handleSelectStudent(st)}>
                        <div className="im-card-name">
                           {st.tenhv}
                        </div>
                        <div className="im-card-sub">SDT: {st.sdt || 'SĐT: Trống'}</div>
                        <div className="im-card-sub">Lớp: {st.malop_list && st.malop_list.length > 0 
                           ? st.malop_list.map(ml => classes.find(c => c.malop === ml)?.tenlop || ml).join(', ')
                           : 'Chưa xếp lớp'}</div>
                     </div>
                  );
               }) : (
                  <div className="im-s-empty">Không tìm thấy học viên hiển thị.</div>
               )}
            </div>
         </div>

         {/* PHẢI: CHI TIẾT VÀ XUẤT PHIẾU */}
         <div className="im-right-pane">
            {message.text && (
               <div className={`message-alert full-width ${message.type}`}>
                  <span>{message.text}</span>
               </div>
            )}

            {!selectedStudent ? (
               <div className="im-empty animate-fade-in">
                  <Receipt size={64} className="text-muted" style={{ opacity: 0.3 }} />
                  <h3>Chưa Chọn Học Viên</h3>
                  <p>Vui lòng nhấp vào một học sinh từ danh sách bên trái để tạo hóa đơn thanh toán.</p>
               </div>
            ) : (
               <div className="invoice-details animate-fade-in">
                  <div className="mobile-header-actions">
                     <button className="btn-back" onClick={() => setShowMobileDetails(false)}>
                        <X size={20} /> Quay lại danh sách
                     </button>
                  </div>

                  <div className="im-sections-wrapper">
                     {/* 1. THÔNG TIN HỌC VIÊN */}
                     {recentSourceText && (
                        <div style={{ background: '#e0f2fe', color: '#0369a1', padding: '10px 15px', borderRadius: '8px', marginBottom: '1.5rem', fontWeight: 600, fontSize: '0.95rem', border: '1px solid #bae6fd' }}>
                           💡 {recentSourceText}
                        </div>
                     )}
                     <div className="im-section">
                        <h3 className="im-section-title"><User size={18} /> Thông tin học sinh</h3>
                        <div className="im-grid-3">
                           <div className="im-field-hz">
                              <label>Mã HV:</label>
                              <div className="val-text">{selectedStudent.mahv}</div>
                           </div>
                           <div className="im-field-hz">
                              <label>Tên HV:</label>
                              <div className="val-text text-primary">{selectedStudent.tenhv}</div>
                           </div>
                           <div className="im-field-hz">
                              <label>SĐT:</label>
                              <div className="val-text text-bold">{selectedStudent.sdt || 'Chưa cung cấp'}</div>
                           </div>
                        </div>
                     </div>

                     {/* 2. CHƯƠNG TRÌNH ĐÀO TẠO */}
                     <div className="im-section">
                        <h3 className="im-section-title"><BookOpen size={18} /> Thông tin lớp học</h3>
                        <div className="im-grid-2">
                           <div className="im-field-hz highlight-field" style={{ background: '#f5f3ff', padding: '12px 15px', borderRadius: '12px', border: '2px solid #6366f1', gridColumn: 'span 2', marginBottom: '10px', flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                              <label style={{ color: '#4338ca', fontWeight: 800, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '6px', minWidth: 'auto', marginBottom: '2px' }}>
                                <AlertCircle size={18} /> CHỌN LỚP XUẤT HÓA ĐƠN:
                              </label>
                              <select 
                                value={activeClass?.malop || ''} 
                                onChange={e => updateClassContext(e.target.value, selectedStudent)}
                                style={{ padding: '0.75rem', borderRadius: '8px', border: '1px solid #c4b5fd', width: '100%', fontWeight: 800, fontSize: '1.1rem', color: '#1e1b4b', cursor: 'pointer', outline: 'none', maxWidth: 'none' }}
                              >
                                {selectedStudent.malop_list && selectedStudent.malop_list.length > 0 ? (
                                   selectedStudent.malop_list.map(ml => (
                                      <option key={ml} value={ml}>{classes.find(c => c.malop === ml)?.tenlop || ml}</option>
                                   ))
                                ) : <option value="">Chưa xếp lớp</option>}
                              </select>
                           </div>
                           <div className="im-field-hz">
                              <label>Giảng Viên:</label>
                              <div className="val-text flex-center"><GraduationCap size={16} /> {classTeacher?.tenhv || classTeacher?.tennv || activeClass?.manv || 'Không rõ'}</div>
                           </div>
                        </div>
                        <div className="im-grid-2 margin-top-1">
                           <div className="im-field-hz">
                              <label>Thu Ngân:</label>
                              <div className="val-text flex-center text-bold text-success"><User size={16} /> {cashier}</div>
                           </div>
                           <div className="im-field-hz">
                              <label>Lịch Học:</label>
                              <div className="val-text">{activeClass?.thoigianbieu || 'Chưa xếp lịch'}</div>
                           </div>
                        </div>

                        <div className="im-grid-2 margin-top-1">
                           <div className="im-field-hz">
                              <label>Đóng Theo:</label>
                              <select value={invoiceData.loaiDong} onChange={e => handleFormChange('loaiDong', e.target.value)}>
                                 {(!config?.tinhhocphi || config.tinhhocphi.selected?.includes('thang')) && <option value="Tháng">Tháng</option>}
                                 {(!config?.tinhhocphi || config.tinhhocphi.selected?.includes('khoa')) && <option value="Khóa">Khóa</option>}
                                 {(!config?.tinhhocphi || config.tinhhocphi.selected?.includes('buoi')) && <option value="Buổi">Từng Buổi</option>}
                                 {(!config?.tinhhocphi || config.tinhhocphi.selected?.includes('tuần')) && <option value="Tuần">Số Tuần</option>}
                              </select>
                           </div>
                           <div className="im-field-hz">
                              <label>Thời lượng:</label>
                              <input type="number" min="1" value={invoiceData.soLuong} onChange={e => handleFormChange('soLuong', e.target.value)} />
                           </div>
                        </div>
                        <div className="im-grid-2 margin-top-1">
                           <div className="im-field-hz">
                              <label>Bắt Đầu:</label>
                              <input type="date" value={invoiceData.ngayBatDau} onChange={e => handleFormChange('ngayBatDau', e.target.value)} />
                           </div>
                           <div className="im-field-hz">
                              <label>Kết Thúc:</label>
                              <input type="date" value={invoiceData.ngayKetThuc} onChange={e => handleFormChange('ngayKetThuc', e.target.value)} />
                           </div>
                        </div>
                     </div>

                     {/* 3. TÀI CHÍNH */}
                     {studySummary && (
                        <div className="study-summary-wrap">
                           <div className="study-summary-label">Thống kê điểm danh — {studySummary.sourceHd || 'gần nhất'}</div>
                           <div className="study-summary-grid">
                              <div className="ss-badge ss-present">
                                 <span className="ss-num">{studySummary.daHoc}</span>
                                 <span className="ss-txt">Đã học</span>
                              </div>
                              <div className="ss-badge ss-excused">
                                 <span className="ss-num">{studySummary.nghiPhep}</span>
                                 <span className="ss-txt">Nghỉ phép</span>
                              </div>
                              <div className="ss-badge ss-absent">
                                 <span className="ss-num">{studySummary.nghiKhongPhep || 0}</span>
                                 <span className="ss-txt">Không phép</span>
                              </div>
                              <div className="ss-badge ss-total">
                                 <span className="ss-num">{studySummary.tongBuoi}</span>
                                 <span className="ss-txt">Tổng buổi</span>
                              </div>
                           </div>
                        </div>
                     )}

                     <div className="im-section">
                        <h3 className="im-section-title"><Wallet size={18} /> Quyết Toán Tổng (VNĐ)</h3>

                        {/* HÀNG 1: NỢ CŨ, HỌC PHÍ, VOUCHER, TIỀN NGHỈ */}
                        <div className={`im-finance-row ${config?.trutiennghi ? 'grid-4' : 'grid-3'}`}>
                           <div className="im-fi-item">
                              <label>Nợ cũ</label>
                              <div className={`fi-val-display ${noCu > 0 ? 'text-danger' : 'text-success'}`}>{formatCurrency(noCu)} ₫</div>
                           </div>
                           <div className="im-fi-item">
                              <label>Học phí</label>
                              <div className="fi-input-wrapper with-chips" style={{ maxWidth: 'unset' }}>
                                 <input type="text" value={formatCurrency(invoiceData.hocphi)} onChange={e => handleFinanceInput('hocphi', e)} />
                                 <span className="unit">₫</span>
                                 {activeClass?.hocphi && (
                                    <div className="tuition-packages-chips">
                                       {activeClass.hocphi.split('\n').filter(Boolean).map((opt, i) => {
                                          const optLower = opt.toLowerCase();
                                          const isBuoi = optLower.includes('buổi');
                                          const isThang = optLower.includes('tháng');
                                          const isKhoa = optLower.includes('khóa');
                                          const isTuan = optLower.includes('tuần');
                                          const sel = config?.tinhhocphi?.selected || ['khoa', 'buoi', 'thang', 'tuần'];
                                          const isAllowed = (isBuoi && sel.includes('buoi')) ||
                                             (isThang && sel.includes('thang')) ||
                                             (isKhoa && sel.includes('khoa')) ||
                                             (isTuan && sel.includes('tuần')) ||
                                             (!isBuoi && !isThang && !isKhoa && !isTuan);
                                          if (!isAllowed) return null;
                                          return (
                                             <span key={i} className="tuition-chip" onClick={() => handleSelectTuitionPackage(opt)}>
                                                {opt.trim()}
                                             </span>
                                          );
                                       })}
                                    </div>
                                 )}
                              </div>
                           </div>
                           <div className="im-fi-item">
                              <label>Giảm HP</label>
                              <div className="fi-input-wrapper" style={{ maxWidth: 'unset' }}>
                                 <input type="text" value={formatCurrency(invoiceData.giamHocphi)} onChange={e => handleFinanceInput('giamHocphi', e)} />
                                 <span className="unit">₫</span>
                              </div>
                           </div>
                           {config?.trutiennghi && tienTruNghi > 0 && (
                              <div className="im-fi-item">
                                 <label style={{ color: '#be123c' }}>Tiền nghỉ ({studySummary?.nghiPhep || 0} p)</label>
                                 <div className="fi-val-display text-bold highlight-deduction">
                                    - {formatCurrency(tienTruNghi)} ₫
                                 </div>
                              </div>
                           )}
                        </div>

                        {/* HÀNG 2: CẦN THU, ĐÃ ĐÓNG, CÒN LẠI */}
                        <div className="im-finance-row grid-3 highlight-row">
                           <div className="im-fi-item">
                              <label>Cần thu</label>
                              <div className="fi-val-display text-bold text-danger" style={{ fontSize: '1.4rem' }}>{formatCurrency(tongCong)} ₫</div>
                           </div>
                           <div className="im-fi-item">
                              <label>Đã đóng</label>
                              <div className="fi-input-wrapper giant" style={{ maxWidth: 'unset' }}>
                                 <input type="text" value={formatCurrency(invoiceData.daDong)} onChange={e => handleFinanceInput('daDong', e)} />
                                 <span className="unit">₫</span>
                              </div>
                           </div>
                           <div className="im-fi-item">
                              <label>Còn lại</label>
                              <div className={`fi-val-display text-bold ${conLai > 0 ? 'text-danger' : 'text-success'}`} style={{ fontSize: '1.25rem' }}>
                                 {formatCurrency(conLai)} ₫
                              </div>
                           </div>
                        </div>

                        {/* HÀNG 3: GHI CHÚ, HÌNH THỨC */}
                        <div className="im-finance-row grid-2">
                           <div className="im-fi-item-col">
                              <label>📝 Ghi chú biên lai</label>
                              <input
                                 className="im-input-text"
                                 type="text"
                                 value={invoiceData.ghiChu}
                                 onChange={e => handleFormChange('ghiChu', e.target.value)}
                                 placeholder="VD: Khách hứa trả nốt vào mùng 5..."
                              />
                           </div>
                           <div className="im-fi-item-col">
                              <label>💳 Hình thức thanh toán</label>
                             <select className="im-select" value={invoiceData.hinhThuc} onChange={e => handleFormChange('hinhThuc', e.target.value)}>
                                {walletsConfig.length === 0 && <option value="Tiền mặt">Tiền mặt</option>}
                                {walletsConfig.map(w => (
                                   <option key={w.id} value={w.name}>{w.name}</option>
                                ))}
                             </select>
                           </div>
                        </div>

                        <div className="im-actions" style={{ display: 'flex', gap: '10px' }}>
                           <button className="im-btn-submit" style={{ background: '#3b82f6', borderColor: '#3b82f6' }} onClick={handleExportNotice}>
                              <MessageSquare size={18} />
                              Xuất Thông Báo
                           </button>
                           <button className="im-btn-submit" onClick={handleSaveInvoice} disabled={isSaving}>
                              <Receipt size={18} />
                              {isSaving ? 'Đang tạo cơ sở dữ liệu...' : 'Xác Nhận Xuất Hóa Đơn'}
                           </button>
                        </div>
                     </div>
                  </div>

               </div>
            )}
         </div>

         {warningModal.isOpen && (
            <div className="im-modal-overlay">
               <div className="im-warning-modal animate-slide-up">
                  <button className="im-close-btn" onClick={() => setWarningModal({ ...warningModal, isOpen: false })}>
                     <X size={20} />
                  </button>
                  <div className="im-warning-icon">
                     <AlertCircle size={52} />
                  </div>
                  <h3>{warningModal.title}</h3>
                  <p>{warningModal.message}</p>
                  <div className="im-warning-actions">
                     <button className="im-btn-warn-ok" onClick={() => setWarningModal({ ...warningModal, isOpen: false })}>
                        Đã Hiểu & Kiểm Tra Lại
                     </button>
                  </div>
               </div>
            </div>
         )}

         {successModal.isOpen && (
            <div className="im-modal-overlay">
               <div className="im-success-modal animate-slide-up">
                  <button
                     className="im-close-btn"
                     onClick={() => setSuccessModal({ ...successModal, isOpen: false })}
                  >
                     <X size={20} />
                  </button>

                  <div className="im-success-icon">
                     <CheckCircle size={52} />
                  </div>

                  <h3>{successModal.title}</h3>
                  <p>{successModal.message}</p>

                  <div className="im-warning-actions">
                     <button
                        className="im-btn-success-ok"
                        onClick={() => setSuccessModal({ ...successModal, isOpen: false })}
                     >
                        OK
                     </button>
                  </div>
               </div>
            </div>
         )}

         {/* HIDDEN TEMPLATE FOR INVOICE PNG EXPORT */}
         <div style={{ position: 'fixed', left: 0, top: 0, width: '100%', height: '100%', overflow: 'hidden', opacity: 0.01, zIndex: -100, pointerEvents: 'none', background: '#ffffff' }}>
            <div id="download-invoice-node" style={{ position: 'relative', overflow: 'hidden', padding: '30px', background: 'white', color: '#000', width: '800px', fontFamily: 'Arial, sans-serif' }}>
               {/* Invoice Template Content ... (remains same) */}
               <div style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 0,
                  opacity: 0.2,
                  pointerEvents: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 10 Q 25 20 50 10 T 100 10' fill='none' stroke='%230066cc' stroke-width='0.5'/%3E%3Cpath d='M0 5 Q 25 15 50 5 T 100 5' fill='none' stroke='%230066cc' stroke-width='0.3' opacity='0.5'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'repeat'
               }} />
               <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%) rotate(-30deg)',
                  fontSize: '60pt',
                  fontWeight: 'bold',
                  color: 'rgba(0, 102, 204, 0.05)',
                  zIndex: 0,
                  pointerEvents: 'none',
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                  width: '150%'
               }}>
                  {config?.tencongty || 'ĐÃ THANH TOÁN'}
               </div>
               <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between' }}>
                     <div>
                        <h3 style={{ margin: 0 }}>{config?.tencongty || 'Tên Công Ty'}</h3>
                        <p style={{ margin: '4px 0' }}>ĐC: {config?.diachicongty}</p>
                        <p style={{ margin: '4px 0' }}>SĐT: {config?.sdtcongty}</p>
                     </div>
                     <div style={{ textAlign: 'right' }}>
                        <div>Mã HĐ: <b>{downloadingInvoice?.mahd}</b></div>
                        <div>Ngày lập: {downloadingInvoice ? new Date(downloadingInvoice.ngaylap).toLocaleDateString('vi-VN') : ''}</div>
                        <img src={config?.logo || "/logo.png"} alt="logo" crossOrigin="anonymous" style={{ width: 80, marginTop: 5 }} />
                     </div>
                  </div>
                  <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "20pt", margin: "15px 0" }}>
                     BIÊN LAI THU HỌC PHÍ
                  </div>
                  <div style={{ fontSize: "14pt", lineHeight: "1.8", margin: '20px 0' }}>
                     <div style={{ display: "flex", justifyContent: "space-between", marginBottom: '5px' }}>
                        <div>Họ và tên: <b>{downloadingInvoice?.tenhv}</b></div>
                        <div>SĐT: <b>{downloadingInvoice?.sdt || ""}</b></div>
                     </div>
                     <div>Khóa học: <b>{downloadingInvoice?.tenlop}</b></div>
                     {downloadingInvoice?.sobuoihoc && (
                        <div>Số buổi/Thời lượng: <b>{downloadingInvoice.sobuoihoc}</b></div>
                     )}
                     <div>
                        Thời lượng: từ <b>{downloadingInvoice?.ngaybatdau ? new Date(downloadingInvoice.ngaybatdau).toLocaleDateString('vi-VN') : ''}</b> đến <b>{downloadingInvoice?.ngayketthuc ? new Date(downloadingInvoice.ngayketthuc).toLocaleDateString('vi-VN') : ''}</b>
                     </div>
                     <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '15px 0' }} />
                     <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <div>Học phí: <b>{downloadingInvoice?.hocphi} đ</b></div>
                        <div>Giảm HP (Voucher): <b>{downloadingInvoice?.giamhocphi} đ</b></div>
                        <div>Nợ cũ: <b>{downloadingInvoice?.nocu} đ</b></div>
                     </div>
                     <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold", marginTop: '5px' }}>
                        <div>Tổng cộng: <b>{downloadingInvoice?.tongcong} đ</b></div>
                        <div>Đã đóng: <b style={{ color: '#059669' }}>{downloadingInvoice?.dadong} đ</b></div>
                        <div>Còn lại: <b style={{ color: '#dc2626' }}>{downloadingInvoice?.conno} đ</b></div>
                     </div>
                     <div style={{ marginTop: '10px' }}>
                        Ghi chú: {downloadingInvoice?.ghichu || ""}
                     </div>
                  </div>
                  <div style={{ marginTop: 40, fontSize: "12pt", display: "flex", justifyContent: "space-between" }}>
                     <div>
                        {config?.tencongty || 'Tên Công Ty'} <br />
                        SĐT/Zalo: {config?.sdtcongty}
                     </div>
                     <div style={{ textAlign: "center" }}>
                        Nhân viên thu tiền <br /><br /><br />
                        <b>{downloadingInvoice?.nhanvien}</b>
                     </div>
                  </div>
                  <div style={{ marginTop: "30px", textAlign: "center", fontStyle: "italic", borderTop: '1px dashed #ccc', paddingTop: '10px', fontSize: '10pt' }}>
                     Lưu ý: Hóa đơn này có giá trị xác nhận việc đóng phí. Vui lòng giữ lại để đối chiếu khi cần thiết.
                  </div>
               </div>
            </div>

            {/* HIDDEN TEMPLATE FOR NOTICE PNG EXPORT */}
            <div id="download-notice-node" className="print-a5-receipt" style={{ width: '800px', background: '#fff', padding: '30px', boxSizing: 'border-box', display: 'block', opacity: 0.01 }}>
               {/* HEADER */}
               <div className="p-header" style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between' }}>
                  <div style={{ textAlign: 'left' }}>
                     <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 900 }}>
                        {config?.tencongty || 'E-Skills Academy'}
                     </h3>
                     <p style={{ margin: '4px 0', fontSize: '14px', fontWeight: 800 }}>{config?.diachicongty}</p>
                     <p style={{ margin: '4px 0', fontSize: '14px', fontWeight: 800 }}>SĐT/Zalo: {config?.sdtcongty}</p>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: '14px', fontWeight: 800 }}>
                     <div>Mã TB: <b style={{ fontWeight: 950 }}>{downloadingNotice?.mahd}</b></div>
                     <div>Ngày lập: <b style={{ fontWeight: 900 }}>{downloadingNotice ? new Date(downloadingNotice.ngaylap).toLocaleDateString("vi-VN") : "..."}</b></div>
                     <img crossOrigin="anonymous" src={config?.logo || "/logo.png"} alt="logo" style={{ width: 100, marginTop: 5 }} onError={(e) => { e.target.src = "/logo.png" }} />
                  </div>
               </div>

               {/* TITLE */}
               <div style={{ textAlign: "center", fontWeight: "950", fontSize: "24pt", margin: "20px 0", color: '#000', textTransform: 'uppercase', textDecoration: 'underline' }}>
                  THÔNG BÁO THU HỌC PHÍ
               </div>

               {/* INFO */}
               <div style={{ fontSize: "15pt", lineHeight: "1.9", color: '#000' }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                     <div>Họ và tên: <b style={{ fontWeight: 950 }}>{downloadingNotice?.tenhv}</b></div>
                     <div>SĐT: <b style={{ fontWeight: 900 }}>{downloadingNotice?.sdt || ""}</b></div>
                  </div>

                  <div>
                     Khóa học: <b style={{ fontWeight: 900 }}>{downloadingNotice?.tenlop}</b>
                  </div>

                  {downloadingNotice?.sobuoihoc && (
                     <div>
                        Số buổi/Thời lượng: <b style={{ fontWeight: 900 }}>{downloadingNotice.sobuoihoc}</b>
                     </div>
                  )}

                  <div>
                     Thời lượng:{" "}
                     từ <b style={{ fontWeight: 900 }}>{downloadingNotice?.ngaybatdau ? new Date(downloadingNotice.ngaybatdau).toLocaleDateString("vi-VN") : "..."}</b>{" "}
                     đến <b style={{ fontWeight: 900 }}>{downloadingNotice?.ngayketthuc ? new Date(downloadingNotice.ngayketthuc).toLocaleDateString("vi-VN") : "..."}</b>
                  </div>

                  {/* FEES */}
                  <div style={{ display: "flex", justifyContent: "space-between", borderTop: '2px solid #000', marginTop: '15px', paddingTop: '10px' }}>
                     <div>Học phí: <b style={{ fontWeight: 900 }}>{downloadingNotice?.hocphi}</b></div>
                     <div>Giảm HP: <b style={{ fontWeight: 900 }}>{downloadingNotice?.giamhocphi}</b></div>
                     <div>Nợ cũ: <b style={{ fontWeight: 800 }}>{formatCurrency(noCu)} đ</b></div>
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "950", borderBottom: '2px solid #000', paddingBottom: '10px', marginBottom: '15px', fontSize: '16pt' }}>
                     <div>Tổng cộng: <b style={{ fontWeight: 950 }}>{downloadingNotice?.tongcong} đ</b></div>
                  </div>

                  <div style={{ marginBottom: '15px' }}>
                     Ghi chú: <b style={{ fontWeight: 800 }}>{downloadingNotice?.ghichu || ""}</b>
                  </div>

                  {/* QR SECTION */}
                  {(() => {
                     const qrUrl = downloadingNotice ? getQRUrl(downloadingNotice, walletsConfig) : null;
                     if (!qrUrl) return (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}>
                           <div style={{ fontWeight: '950', fontSize: '14pt' }}>Hình thức thanh toán: <span style={{ color: '#000' }}>{downloadingNotice?.hinhthuc}</span></div>
                        </div>
                     );
                     return (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', marginTop: '10px' }}>
                           <div style={{ fontWeight: '950', fontSize: '14pt', marginBottom: '10px', textAlign: 'right', width: '100%' }}>Hình thức thanh toán: <span style={{ color: '#000' }}>{downloadingNotice?.hinhthuc}</span></div>
                           <div style={{ textAlign: 'center' }}>
                              <img crossOrigin="anonymous" src={qrUrl} alt="Mã QR" style={{ width: '280px', height: '280px', borderRadius: '12px', border: '4px solid #000' }} />
                              <div style={{ fontSize: '12pt', textAlign: 'center', marginTop: '8px', color: '#000', fontWeight: 950 }}>QUÉT MÃ QR ĐỂ THANH TOÁN</div>
                           </div>
                        </div>
                     );
                  })()}
               </div>

               {/* FOOTER */}
               <div style={{ marginTop: 20, fontSize: "15pt", display: "flex", justifyContent: "space-between", alignItems: 'flex-end' }}>
                  <div style={{ lineHeight: '1.6' }}>
                     <b style={{ fontWeight: 950, fontSize: '17pt' }}>{config?.tencongty || 'E-Skills Academy'} </b><br />
                     Hotline: <b style={{ fontWeight: 900 }}>{config?.sdtcongty}</b><br />
                     Nhân viên: <b style={{ fontWeight: 950 }}>{cashier}</b>
                  </div>
                  <div style={{ textAlign: "right", fontSize: '12pt', fontStyle: 'italic', opacity: 0.8 }}>
                     (Xác nhận)
                  </div>
               </div>
            </div>
         </div>

         {previewImg && (
            <div className="sp-modal-overlay" onClick={() => setPreviewImg(null)} style={{ zIndex: 3000, position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.7)', padding: '15px' }}>
               <div className="sp-success-modal animate-slide-up" onClick={e => e.stopPropagation()} style={{ padding: '20px', maxWidth: '100%', width: '450px', background: 'white', borderRadius: '12px', position: 'relative' }}>
                  <button onClick={() => setPreviewImg(null)} style={{ position: 'absolute', right: 10, top: 10, border: 'none', background: 'transparent', cursor: 'pointer' }}><X size={20} /></button>
                  <p style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: '10px', color: '#0369a1', fontSize: '1rem' }}>
                     NHẤN GIỮ HÌNH ĐỂ LƯU / CHIA SẺ HÓA ĐƠN
                  </p>
                  <img src={previewImg} alt="Preview Invoice" style={{ width: '100%', maxHeight: '65vh', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} />
                  <div style={{ marginTop: '15px', textAlign: 'center' }}>
                     <button className="im-btn-submit" style={{ width: '100%' }} onClick={() => setPreviewImg(null)}>HOÀN TẤT</button>
                  </div>
               </div>
            </div>
         )}
      </div>
   );
}
